/** Bestätigungsdialog im Modal der App.
 *
 *  Ersetzt das native confirm(): Browser dürfen dessen Dialoge unterdrücken, und
 *  ein unterdrücktes confirm() liefert stumm false. Eine zerstörende Aktion würde
 *  dann wortlos ausbleiben, ohne Fehler und ohne Hinweis.
 *
 *  Aus einem offenen Modal heraus aufrufbar: der Dialog weicht dann auf die
 *  zweite Modal-Ebene aus und legt sich darüber, statt das Arbeitsfenster zu
 *  verdrängen. Die Ebene wird automatisch gewählt, die Aufrufstellen müssen
 *  nichts davon wissen.
 */

import {
  openModal, closeModal, modalBody, onModalClose, isModalOpen,
  openModal2, closeModal2, modalBody2, onModal2Close,
} from "./modal";
import { esc } from "../utils/html";

/** Ebene 1, solange sie frei ist, sonst die darueberliegende Ebene 2. */
function layer() {
  return isModalOpen()
    ? { open: openModal2, close: closeModal2, body: modalBody2, onClose: onModal2Close }
    : { open: openModal, close: closeModal, body: modalBody, onClose: onModalClose };
}

export interface ConfirmOptions {
  title: string;
  message: string;
  /** Beschriftung der bestätigenden Schaltfläche. Vorgabe: "OK". */
  confirmLabel?: string;
  /** Rote Schaltfläche für zerstörende Aktionen. */
  danger?: boolean;
}

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>(resolve => {
    const confirmLabel = opts.confirmLabel || "OK";
    const confirmClass = opts.danger ? "btn-danger-fill" : "btn-primary";
    const L = layer();

    L.open(opts.title, `
      <p style="font-size:.875rem;color:var(--gray-700);white-space:pre-line">${esc(opts.message)}</p>
      <div class="form-actions">
        <button class="btn btn-secondary" id="cd-cancel">Abbrechen</button>
        <button class="btn ${confirmClass}" id="cd-ok">${esc(confirmLabel)}</button>
      </div>`);

    let decided = false;
    const decide = (value: boolean) => {
      if (decided) return;
      decided = true;
      resolve(value);
    };

    // Deckt Escape, Klick auf den Hintergrund und das X ab: alles gilt als Abbruch.
    // Nach einer Entscheidung greift das dank decided nicht mehr.
    L.onClose(() => decide(false));

    const mb = L.body();
    const cancelBtn = mb.querySelector("#cd-cancel") as HTMLButtonElement;
    const okBtn = mb.querySelector("#cd-ok") as HTMLButtonElement;
    cancelBtn.addEventListener("click", () => { decide(false); L.close(); });
    okBtn.addEventListener("click", () => { decide(true); L.close(); });
    // Bei zerstörenden Aktionen liegt der Fokus auf "Abbrechen": ein reflexhaftes
    // Enter darf nichts löschen.
    (opts.danger ? cancelBtn : okBtn).focus();
  });
}

export interface ChoiceOptions<K extends string> {
  title: string;
  message: string;
  /** Wahlmöglichkeiten von links nach rechts, "Abbrechen" steht immer davor.
   *  truncate kürzt einen langen label mit "…" (voller Text im Tooltip). */
  choices: { key: K; label: string; danger?: boolean; truncate?: boolean }[];
}

/** Rückfrage mit mehreren Antworten, etwa "nur hier" oder "überall" löschen.
 *  Liefert den gewählten key, bei Abbruch null. Der Fokus liegt auf "Abbrechen",
 *  damit ein reflexhaftes Enter nichts auslöst. Öffnet im extrabreiten Fenster, damit die
 *  ausführlich beschrifteten Knöpfe in einer Reihe stehen; nur auf schmalen
 *  Bildschirmen brechen sie um. */
export function choiceDialog<K extends string>(opts: ChoiceOptions<K>): Promise<K | null> {
  return new Promise<K | null>(resolve => {
    const L = layer();
    const buttons = opts.choices.map((c, i) => c.truncate
      ? `<button class="btn btn-truncate ${c.danger ? "btn-danger-fill" : "btn-secondary"}" data-choice="${i}" title="${esc(c.label)}"><span>${esc(c.label)}</span></button>`
      : `<button class="btn ${c.danger ? "btn-danger-fill" : "btn-secondary"}" data-choice="${i}">${esc(c.label)}</button>`
    ).join("");

    L.open(opts.title, `
      <p style="font-size:.875rem;color:var(--gray-700);white-space:pre-line">${esc(opts.message)}</p>
      <div class="form-actions form-actions-wrap">
        <button class="btn btn-secondary" id="cd-cancel">Abbrechen</button>
        ${buttons}
      </div>`, { wider: true });

    let decided = false;
    const decide = (value: K | null) => {
      if (decided) return;
      decided = true;
      resolve(value);
    };
    L.onClose(() => decide(null));

    const mb = L.body();
    const cancelBtn = mb.querySelector("#cd-cancel") as HTMLButtonElement;
    cancelBtn.addEventListener("click", () => { decide(null); L.close(); });
    mb.querySelectorAll<HTMLButtonElement>("[data-choice]").forEach(btn => {
      btn.addEventListener("click", () => { decide(opts.choices[+btn.dataset.choice!].key); L.close(); });
    });
    cancelBtn.focus();
  });
}

/** Hinweisdialog ohne Wahl. Für Meldungen, die eine Aktion abgelehnt haben und zu
 *  wichtig für einen Toast sind, etwa ein blockiertes Löschen mit Begründung. */
export function infoDialog(title: string, message: string): Promise<void> {
  return new Promise<void>(resolve => {
    const L = layer();

    L.open(title, `
      <p style="font-size:.875rem;color:var(--gray-700);white-space:pre-line">${esc(message)}</p>
      <div class="form-actions">
        <button class="btn btn-primary" id="cd-ok">Verstanden</button>
      </div>`);

    let done = false;
    const finish = () => { if (done) return; done = true; resolve(); };
    L.onClose(finish);

    const okBtn = L.body().querySelector("#cd-ok") as HTMLButtonElement;
    okBtn.addEventListener("click", () => { finish(); L.close(); });
    okBtn.focus();
  });
}
